import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Bell, CheckCheck } from 'lucide-react';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read when clicked
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'message':
        // Navigate to messages with the sender
        if (notification.related_id) {
          navigate(`/messages?userId=${notification.related_id}`);
        } else {
          navigate('/messages');
        }
        break;
      
      case 'purchase':
        // Navigate to document detail or earnings page
        if (notification.related_id) {
          navigate(`/document/${notification.related_id}`);
        } else {
          navigate('/earnings');
        }
        break;
      
      case 'rating':
        // Navigate to the document that was rated
        if (notification.related_id) {
          navigate(`/document/${notification.related_id}`);
        }
        break;
      
      default:
        // For other types, just mark as read
        break;
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'purchase':
        return '💰';
      case 'rating':
        return '⭐';
      case 'message':
        return '💬';
      default:
        return '🔔';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            variant="ghost"
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {notifications.some(n => !n.read) && (
            <Button
              data-testid="mark-all-read-btn"
              onClick={markAllAsRead}
              variant="outline"
              className="rounded-xl"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
              Notifications
            </h1>
          </div>

          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                data-testid={`notification-${notification.id}`}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
                  notification.read
                    ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1">
                      <p className="text-gray-800 font-medium mb-1">{notification.content}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                      {!notification.read && (
                        <p className="text-xs text-blue-600 font-medium mt-1">
                          Click to view →
                        </p>
                      )}
                    </div>
                  </div>
                  {!notification.read && (
                    <Button
                      data-testid={`mark-read-${notification.id}`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the card click
                        markAsRead(notification.id);
                      }}
                      size="sm"
                      variant="ghost"
                      className="rounded-xl"
                    >
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {notifications.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p>No notifications</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
